import { supabase } from '../lib/supabase';
import imageCompression from 'browser-image-compression';
import piexif from 'piexifjs';
import { validateFileUpload, checkRateLimit } from './securityService';

/**
 * Generate SHA256 hash of file for integrity verification
 */
export async function generateFileHash(file) {
  try {
    const arrayBuffer = await file?.arrayBuffer();
    const hashBuffer = await crypto.subtle?.digest('SHA-256', arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray?.map(b => b?.toString(16)?.padStart(2, '0'))?.join('');
    return { success: true, hash: hashHex };
  } catch (error) {
    console.error('Hash generation error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Strip EXIF data from image using piexifjs
 */
export async function stripEXIFData(file) {
  try {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const dataUrl = e.target.result;
          
          // Remove EXIF data
          const stripped = piexif.remove(dataUrl);
          
          // Convert back to blob
          fetch(stripped)
            .then(res => res.blob())
            .then(blob => {
              const strippedFile = new File([blob], file.name, { type: file.type });
              resolve({ success: true, file: strippedFile });
            })
            .catch(err => reject(err));
        } catch (error) {
          // If EXIF stripping fails, return original file
          console.warn('EXIF stripping failed, using original:', error);
          resolve({ success: true, file });
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  } catch (error) {
    console.error('EXIF strip error:', error);
    return { success: true, file }; // Return original on error
  }
}

/**
 * Compress image with browser-image-compression
 */
export async function compressImage(file, options = {}) {
  try {
    const defaultOptions = {
      maxSizeMB: 2,
      maxWidthOrHeight: 2048,
      useWebWorker: true,
      preserveExif: false,
      fileType: file?.type,
      ...options
    };

    const compressedFile = await imageCompression(file, defaultOptions);
    return { success: true, file: compressedFile };
  } catch (error) {
    console.error('Compression error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Generate thumbnail for image
 */
export async function generateThumbnail(file) {
  try {
    const options = {
      maxSizeMB: 0.1,
      maxWidthOrHeight: 300,
      useWebWorker: true,
      preserveExif: false,
      fileType: 'image/jpeg'
    };

    const thumbnail = await imageCompression(file, options);
    return { success: true, file: thumbnail };
  } catch (error) {
    console.error('Thumbnail generation error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Process image for upload: strip EXIF, compress, generate thumbnail, hash
 */
export async function processImageForUpload(file) {
  try {
    // Step 1: Strip EXIF data
    const strippedResult = await stripEXIFData(file);
    let processedFile = strippedResult?.success ? strippedResult?.file : file;

    // Step 2: Compress image
    const compressResult = await compressImage(processedFile);
    if (compressResult?.success) {
      processedFile = compressResult?.file;
    }

    // Step 3: Generate thumbnail
    const thumbnailResult = await generateThumbnail(file);

    // Step 4: Generate hash
    const hashResult = await generateFileHash(processedFile);

    return {
      success: true,
      processedFile,
      thumbnail: thumbnailResult?.success ? thumbnailResult?.file : null,
      hash: hashResult?.success ? hashResult?.hash : null
    };
  } catch (error) {
    console.error('Image processing error:', error);
    return {
      success: false,
      error: error?.message || 'Failed to process image'
    };
  }
}

/**
 * Upload file to Supabase Storage bucket
 */
export async function uploadToStorage(bucket, path, file, options = {}) {
  try {
    const { data, error } = await supabase?.storage?.from(bucket)?.upload(path, file, {
        cacheControl: '3600',
        upsert: false,
        ...options
      });

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Storage upload error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Generate signed URL for secure file access
 */
export async function getSignedUrl(bucket, path, expiresIn = 3600) {
  try {
    const { data, error } = await supabase?.storage?.from(bucket)?.createSignedUrl(path, expiresIn);

    if (error) throw error;

    return { success: true, url: data?.signedUrl };
  } catch (error) {
    console.error('Signed URL error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Upload case file with full processing pipeline
 */
export async function uploadCaseFile(userId, caseId, file) {
  try {
    // Check rate limit
    const rateLimitResult = await checkRateLimit('file_upload');
    if (!rateLimitResult?.allowed) {
      throw new Error('Upload rate limit exceeded. Please try again later.');
    }

    // Validate file
    const validation = await validateFileUpload(file);
    if (!validation?.valid) {
      throw new Error(validation?.error);
    }

    // For images: strip EXIF, compress to 2MB/2048px, generate thumbnail+hash
    let fileToUpload = file;
    if (file?.type?.startsWith('image/')) {
      const processed = await processImageForUpload(file);
      if (processed?.success) {
        fileToUpload = processed.processedFile;
      }
    }

    const fileExt = fileToUpload?.name?.split('.')?.pop();
    const fileName = `${Date.now()}_${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
    const filePath = `${userId}/${caseId}/${fileName}`;

    const { data, error } = await supabase?.storage
      ?.from('case-uploads')
      ?.upload(filePath, fileToUpload, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) throw error;

    // Record upload in database
    const { data: uploadRecord, error: dbError } = await supabase
      ?.from('case_uploads')
      ?.insert({
        case_id: caseId,
        file_name: file?.name,
        file_type: fileToUpload?.type,
        file_size: fileToUpload?.size,
        storage_path: data?.path,
        is_image: file?.type?.startsWith('image/')
      })
      ?.select()
      ?.single();

    if (dbError) throw dbError;

    return { success: true, data: uploadRecord };
  } catch (error) {
    console.error('Upload case file error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Upload multiple files with progress tracking
 */
export async function uploadMultipleFiles(userId, caseId, files, onProgress) {
  const results = [];
  let successCount = 0;
  let failCount = 0;

  for (let i = 0; i < files?.length; i++) {
    const file = files?.[i];
    const fileType = file?.type?.startsWith('image/') ? 'image' : 'document';
    
    const result = await uploadCaseFile(userId, caseId, file, fileType);
    results?.push({ file: file?.name, ...result });

    if (result?.success) {
      successCount++;
    } else {
      failCount++;
    }

    // Report progress
    if (onProgress) {
      onProgress({
        current: i + 1,
        total: files?.length,
        successCount,
        failCount,
        percentage: Math.round(((i + 1) / files?.length) * 100)
      });
    }
  }

  return {
    success: failCount === 0,
    results,
    summary: {
      total: files?.length,
      success: successCount,
      failed: failCount
    }
  };
}

/**
 * Get signed URL for case upload
 */
export async function getCaseFileUrl(storagePath, bucket = 'case-images') {
  return getSignedUrl(bucket, storagePath, 3600);
}

/**
 * Delete file from storage
 */
export async function deleteFromStorage(bucket, path) {
  try {
    const { error } = await supabase?.storage?.from(bucket)?.remove([path]);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Storage delete error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Upload a user profile avatar.
 * Stores in "avatars" bucket as `{userId}.webp`, upserts so re-uploads replace the old file.
 * Returns the public URL on success.
 */
export async function uploadAvatar(userId, file) {
  try {
    if (!file) throw new Error('No file provided');

    // Validate basic image type
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      throw new Error('Only JPEG, PNG, WebP and GIF images are allowed');
    }
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('Image must be under 5 MB');
    }

    // Compress
    const compressed = await imageCompression(file, {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 512,
      useWebWorker: true,
      fileType: 'image/webp',
    });

    const filePath = `${userId}.webp`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, compressed, {
        contentType: 'image/webp',
        cacheControl: '3600',
        upsert: true,
      });
    if (uploadError) throw uploadError;

    // Get public URL
    const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(filePath);
    const publicUrl = urlData?.publicUrl ? `${urlData.publicUrl}?t=${Date.now()}` : null;

    return { success: true, url: publicUrl };
  } catch (error) {
    console.error('Avatar upload error:', error);
    return { success: false, error: error?.message || 'Upload failed' };
  }
}

/**
 * Upload a certification badge / logo image.
 * Stored in "certification-badges" bucket under `{userId}/{uniqueId}.webp`.
 * Compresses to ≤256px WebP before uploading.
 * Returns the public URL on success.
 */
export async function uploadCertBadge(userId, file) {
  try {
    if (!file) throw new Error('No file provided');

    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      throw new Error('Only JPEG, PNG, WebP, GIF and SVG images are allowed');
    }
    if (file.size > 2 * 1024 * 1024) {
      throw new Error('Image must be under 2 MB');
    }

    // SVGs don't need compression – upload as-is
    let uploadBlob = file;
    let contentType = file.type;
    let ext = 'svg';

    if (file.type !== 'image/svg+xml') {
      uploadBlob = await imageCompression(file, {
        maxSizeMB: 0.3,
        maxWidthOrHeight: 256,
        useWebWorker: true,
        fileType: 'image/webp',
      });
      contentType = 'image/webp';
      ext = 'webp';
    }

    const uniqueId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const filePath = `${userId}/${uniqueId}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('certification-badges')
      .upload(filePath, uploadBlob, {
        contentType,
        cacheControl: '86400',
        upsert: false,
      });
    if (uploadError) throw uploadError;

    const { data: urlData } = supabase.storage.from('certification-badges').getPublicUrl(filePath);
    const publicUrl = urlData?.publicUrl ? `${urlData.publicUrl}?t=${Date.now()}` : null;

    return { success: true, url: publicUrl };
  } catch (error) {
    console.error('Cert badge upload error:', error);
    return { success: false, error: error?.message || 'Upload failed' };
  }
}